import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, Puzzle } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@orisini/ui/components/card'

interface ManifesUnduhan {
  version: string
  file: string
  bytes: number
}

/**
 * Halaman unduh ekstensi Chrome Pinboss.
 *
 * Ada di Pinboss, BUKAN di orisini.com. Agent desktop dipakai semua produk
 * sehingga unduhannya milik platform; ekstensi sebaliknya — tiap produk punya
 * ekstensinya sendiri karena perilaku tiap situs berbeda, jadi tempatnya di
 * produk yang bersangkutan.
 *
 * Artifaknya aset statis (`public/unduhan/`), bukan objek R2 seperti installer
 * agent: ukurannya kilobyte, bukan puluhan megabyte, jadi tidak ada alasan
 * menambah bucket, manifes rilis, maupun jalur unggah tersendiri. Versinya
 * dibaca dari manifes yang ditulis build ekstensi supaya halaman ini tidak
 * perlu disunting tiap rilis.
 */
export default function ExtensionPage() {
  const [manifes, setManifes] = useState<ManifesUnduhan | null>(null)
  const [gagal, setGagal] = useState(false)

  useEffect(() => {
    fetch('/unduhan/ekstensi.json')
      .then((res) => (res.ok ? (res.json() as Promise<ManifesUnduhan>) : Promise.reject(res.status)))
      .then(setManifes)
      .catch(() => setGagal(true))
  }, [])

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Chrome extension</h1>
        <p className="text-sm text-muted-foreground">
          Grab images from any page and add them straight to your slot queue
        </p>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Puzzle className="size-4" />
            Pinboss untuk Chrome
            {manifes && (
              <span className="text-sm font-normal text-muted-foreground">
                v{manifes.version} · {(manifes.bytes / 1024).toFixed(0)} KB
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Open an article or product page, scan its images, fill in the pin details, then
            send it to a slot — without downloading the images one by one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {gagal && (
            <p className="text-sm text-destructive">
              The extension package isn't available on this server yet. Try reloading the page.
            </p>
          )}
          {/* Tombol baru dirender setelah manifesnya ada: `disabled` pada
              Button asChild jatuh ke <a> yang mengabaikannya, jadi tombolnya
              akan terlihat aktif padahal href-nya belum diketahui. */}
          {manifes && (
            <Button asChild>
              <a href={`/unduhan/${manifes.file}`} download>
                <Download className="size-4" />
                Download extension (.zip)
              </a>
            </Button>
          )}
          <p className="text-sm text-muted-foreground">Requires Google Chrome 114 or newer.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">How to install</CardTitle>
          <CardDescription>
            This extension isn't on the Chrome Web Store yet, so installing is manual — just once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              Extract the zip file. It contains a single folder named{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">pinboss-extension</code> —
              keep it somewhere it won't be deleted, because Chrome loads it from there every time
              it starts.
            </li>
            <li>
              Open <code className="rounded bg-muted px-1 py-0.5 text-xs">chrome://extensions</code>{' '}
              in Chrome.
            </li>
            <li>
              Turn on <strong className="text-foreground">Developer mode</strong> in the top right
              corner.
            </li>
            <li>
              Click <strong className="text-foreground">Load unpacked</strong>, then select that
              folder.
            </li>
            <li>
              Click the Pinboss icon in the Chrome toolbar, then sign in with your{' '}
              <strong className="text-foreground">Orisini account</strong> — the same account you're
              using now.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Before use</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Create slots first.</strong> The extension adds
            images to the queue, and the queue needs a schedule — without an active slot, pins just
            pile up as drafts. Set them up in <Link to="/slots" className="underline">Slots</Link>.
          </p>
          <p>
            Chrome will warn that the extension can “read and change data on all sites”. That is
            indeed the permission it asks for: image scanning has to work on any page, and this
            extension doesn't send anything before you press a button.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
